import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

// Inline SVG icon
const ArrowRightIcon = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

export type ContentType = 'clip' | 'quote' | 'article' | 'social';

export interface ContentCardProps {
  id: string;
  storyId: string;
  title: string;
  thumbnail?: string;
  contentType: ContentType;
  createdDate?: string;
  onClick?: () => void;
}

const contentTypeLabels: Record<ContentType, string> = {
  clip: 'Clip',
  quote: 'Quote',
  article: 'Article',
  social: 'Social Post',
};

const contentTypeColors: Record<ContentType, { bg: string; text: string }> = {
  clip: { bg: 'var(--content-clips-bg)', text: 'var(--content-clips-text)' },
  quote: { bg: 'var(--content-quotes-bg)', text: 'var(--content-quotes-text)' },
  article: {
    bg: 'var(--content-articles-bg)',
    text: 'var(--content-articles-text)',
  },
  social: {
    bg: 'var(--content-social-bg)',
    text: 'var(--content-social-text)',
  },
};

export const ContentCard = observer(
  ({
    id,
    title,
    thumbnail,
    contentType,
    createdDate,
    onClick,
  }: ContentCardProps) => {
    return (
      <Card onClick={onClick} data-testid={`home-content-row-${id}`}>
        <ThumbnailContainer>
          {thumbnail ? (
            <Thumbnail src={thumbnail} alt={title} />
          ) : (
            <NoThumbnail>No preview</NoThumbnail>
          )}
          <HoverOverlay className="hover-overlay">
            <ViewButton
              onClick={e => {
                e.stopPropagation();
                onClick?.();
              }}
            >
              <span>View</span>
              <ArrowRightIcon />
            </ViewButton>
          </HoverOverlay>
          <TypeBadge
            $bgColor={contentTypeColors[contentType].bg}
            $textColor={contentTypeColors[contentType].text}
          >
            {contentTypeLabels[contentType]}
          </TypeBadge>
        </ThumbnailContainer>

        <InfoSection>
          <Title>{title}</Title>
          {createdDate && <CreatedDate>{createdDate}</CreatedDate>}
        </InfoSection>
      </Card>
    );
  },
);

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 180px;
  min-width: 180px;
  background-color: var(--surface);
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: box-shadow var(--transition-normal) ease;

  &:hover {
    box-shadow: var(--elevation-sm);
  }

  &:hover .hover-overlay {
    opacity: 1;
  }
`;

const ThumbnailContainer = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%; /* 16:9 aspect ratio */
  background-color: var(--gray-500);
  overflow: hidden;
`;

const Thumbnail = styled.img`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const NoThumbnail = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--border);
  font-size: 11px;
`;

const HoverOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity var(--transition-normal) ease;
`;

const ViewButton = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 4px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  background-color: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: white;
  cursor: pointer;
  transition: all var(--transition-normal) ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.25);
    border-color: rgba(255, 255, 255, 0.5);
  }
`;

const TypeBadge = styled.div<{ $bgColor: string; $textColor: string }>`
  position: absolute;
  top: 8px;
  left: 8px;
  padding: 4px 8px;
  background-color: ${props => props.$bgColor};
  border-radius: 4px;
  font-size: var(--text-xs);
  font-weight: var(--font-weight-semibold);
  color: ${props => props.$textColor};
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  padding: 10px;
`;

const Title = styled.h4`
  font-size: 13px;
  font-weight: var(--font-weight-medium);
  color: var(--foreground);
  margin: 0 0 4px 0;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
`;

const CreatedDate = styled.p`
  font-size: 11px;
  font-weight: var(--font-weight-regular);
  color: var(--text-muted);
  margin: 0;
`;
